/**
 * useAnonymizationsByEntity / useCreateAnonymization / useExecuteAnonymization /
 * useCompleteAnonymization — Brief 5 B5 Anonimização (Sprint Frontend Brief 5 · Tela 3).
 *
 * Consome o módulo lgpd (anonymization.service / lgpd.controller):
 *   - GET  /v1/lgpd/anonymizations?entityType=&entityId=  → lista por entidade
 *   - POST /v1/lgpd/anonymizations                        → criar (status pending)
 *   - POST /v1/lgpd/anonymizations/:id/execute            → pending → in_progress
 *   - POST /v1/lgpd/anonymizations/:id/complete           → in_progress → completed|failed
 *
 * Gate server-side: `lgpd-anonymization-execute` (Professional+, requires
 * lgpd-requests-management). `lgpd-requests-read` na listagem.
 *
 * P15 (Privacidade desde o desenho): ação irreversível (execute) — sem optimistic
 * update; espera confirmação do servidor. Mutations invalidam a lista da entidade.
 *
 * 🔴 NÃO reusa `use-lgpd-anonymization.ts` (contrato drifted — ver types.ts).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api-client';

import type {
  AnonymizationPreviewPlan,
  AnonymizationWireItem,
  CompleteAnonymizationRequest,
  CreateAnonymizationRequest,
  PreviewAnonymizationRequest,
} from '../features/dpo/anonimizacao/types';

const QUERY_KEY = 'anonymizations-by-entity';
const STALE_TIME = 15_000;

export interface EntityRef {
  entityType: string;
  entityId: string;
}

export function useAnonymizationsByEntity(entity: EntityRef | null): {
  data: AnonymizationWireItem[];
  isLoading: boolean;
  isError: boolean;
} {
  const query = useQuery({
    queryKey: [QUERY_KEY, entity?.entityType ?? null, entity?.entityId ?? null],
    queryFn: () => api.listAnonymizationsByEntity(entity!.entityType, entity!.entityId),
    enabled: entity !== null,
    staleTime: STALE_TIME,
    retry: 1,
  });
  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

/**
 * Dry-run NÃO-destrutivo (Fatia 1 motor ADR-110). Mutation user-triggered
 * (dispara só no clique — fetch inerentemente gated; a página já é gated
 * pela cap `lgpd-anonymization-execute`, D-B). Zero mutação no BE.
 */
export function usePreviewAnonymization(): ReturnType<
  typeof useMutation<AnonymizationPreviewPlan, Error, PreviewAnonymizationRequest>
> {
  return useMutation<AnonymizationPreviewPlan, Error, PreviewAnonymizationRequest>({
    mutationFn: (body) => api.previewAnonymization(body),
  });
}

export function useCreateAnonymization(): ReturnType<
  typeof useMutation<AnonymizationWireItem, Error, CreateAnonymizationRequest>
> {
  const qc = useQueryClient();
  return useMutation<AnonymizationWireItem, Error, CreateAnonymizationRequest>({
    mutationFn: (body) => api.createAnonymizationFull(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

export function useExecuteAnonymization(): ReturnType<
  typeof useMutation<AnonymizationWireItem, Error, string>
> {
  const qc = useQueryClient();
  return useMutation<AnonymizationWireItem, Error, string>({
    mutationFn: (id) => api.executeAnonymizationFull(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

interface CompleteVars {
  id: string;
  body: CompleteAnonymizationRequest;
}

export function useCompleteAnonymization(): ReturnType<
  typeof useMutation<AnonymizationWireItem, Error, CompleteVars>
> {
  const qc = useQueryClient();
  return useMutation<AnonymizationWireItem, Error, CompleteVars>({
    mutationFn: ({ id, body }) => api.completeAnonymization(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
